/**
 * What's new in virta, for the agents that use it (owner, 2026-10-01: an
 * onboarded project didn't know the CLI took files). `virta brief` — the
 * session-start hook every onboarded repo runs — prints the entries newer
 * than the last this identity saw, once, then stays quiet. Add an entry
 * whenever a capability an agent would reach for lands; keep each one a
 * line an agent can act on: what, and the command.
 */
export interface NewsItem {
  /** when it landed (YYYY-MM-DD): what "newer than you saw" compares */
  date: string
  text: string
}

export const NEWS: readonly NewsItem[] = [
  {
    date: '2026-09-28',
    text: 'One CLI grammar: a task is the implicit noun (`virta close #n`); `project …` and `self …` come first. `virta <verb> --help` shows flags and examples.',
  },
  {
    date: '2026-09-28',
    text: 'Many edits in one call: `virta apply ops.json` (all or nothing; `--dry` first). MCP: the apply tool.',
  },
  {
    date: '2026-09-30',
    text: 'Files on tasks: `virta attach #n ./shot.png` (images, video, audio, PDF, markdown, text; 25 MB). MCP: attach with content (base64) and name.',
  },
  {
    date: '2026-09-30',
    text: 'Showcase finished work: `virta showcase #n "what it does" --media ./shot.png --link <docs>` puts it in the board\'s carousel.',
  },
  {
    date: '2026-09-30',
    text: 'Practices live on the board (the Library): `virta new "…" --type practice --topics a,b`, `virta measure #n …`, `virta procedure #n --when … --set …`, `virta project practices`.',
  },
  {
    date: '2026-10-01',
    text: "Can't store files (\"can't reach virta:files\")? Your token was narrowed to the event log: run `virta self update`, then `virta self login` — from an agent's shell it polls, so a person can approve on any device, a phone included.",
  },
  {
    date: '2026-10-01',
    text: 'Edits never overwrite each other: a concurrent edit is kept and flagged on the card; `virta versions #n` shows every version with diffs.',
  },
  {
    date: '2026-10-01',
    text: 'Projects: `virta self login --this-repo` gives a repo its own token, so its writes carry its name; `virta project set <name> --icon ./logo.svg` keeps an SVG icon as SVG; `--version 0.3.0` now sets the version.',
  },
  {
    date: '2026-10-01',
    text: 'Practices: `virta export <dir>` / `virta restore <dir> --dry` back them up (registry/: an .md per entry, the index, the full event log); `virta split #A "t1" "t2"`, `supersede #A #B`, `merge #A #B --into #C` refactor them; retire with `close --as retired "why"`; `virta guide [topic]` is the way in (root, topic indexes, project-first precedence; MCP resource virta://guidance).',
  },
  {
    date: '2026-10-02',
    text:
      'Notices: your brief shows what the ecosystem asks of your repo; `virta project notices` checks and records it (adopt / na for the rest). ' +
      'Wrong name on your writes (a token labelled for another repo)? `virta self relabel "<machine>"` corrects them, past and future, by appending: no new login. Backups: `virta backup <dir>` / `virta restore-backup <dir>`.',
  },
  {
    date: '2026-10-06',
    text: 'Parent tasks: `virta child #12 "title"` files a part of #12 (or `virta set #40 parent=#12`). A parent follows its least complete part automatically and closes when all are done, so don\'t set its status yourself. Scripts: `n=$(virta new "…" --quiet)` gives just the number.',
  },
  {
    date: '2026-10-08',
    text: 'Skills are served by the board, one copy: `virta skill list`, `virta skill run <name>` (prints the current version and what changed since this repo last ran it), `virta skill install <name>` (a stub in ~/.claude/skills), `virta skill pin <name>` to finish a release on one version.',
  },
  {
    date: '2026-10-08',
    text: 'Filing a task lists similar open tasks in its project (it never blocks; close yours as duplicate if one is the same). To look first: `virta similar "title"`. Wider, bodies too: `virta list "~word ~other"`.',
  },
  {
    date: '2026-10-13',
    text: 'A project can be public (its owner sets it on the board): its tile, with name, description, links and counts, shows to anyone; tasks never do. Your writes keep that summary current by themselves; `virta project publish` does it on request. `virta project push` now means the repo you are in (`--all` for every project).',
  },
  {
    date: '2026-11-01',
    text: "Read a card's attachments: `virta files #n` lists them, `virta files #n --out ./dir` downloads each, checked against its sha256. MCP: attachments, with content: true for small files inline.",
  },
  {
    date: '2026-11-02',
    text: 'A repo joins the board in one owner command: `virta project setup` (the hooks, the MCP server and the import, behind one preview and one yes). Run `virta project setup --dry` and hand the owner `! virta project setup`.',
  },
  {
    date: '2026-11-02',
    text: "A Stop hook: `virta brief --stop` asks you once per turn to commit, push and take the next ready task instead of ending on 'next up'. Waiting on the owner? File the question as a kind:question card and stop. Your repo gets it when the owner re-runs `virta project setup`.",
  },
]

/** The news newer than `seen` (a date, '' for none seen), newest last, at most `max`. */
export function newsSince(seen: string, max = 8): NewsItem[] {
  return NEWS.filter((n) => n.date > seen).slice(-max)
}

/** The newest date in the news: what a brief records as seen. */
export const LATEST_NEWS = NEWS.reduce((d, n) => (n.date > d ? n.date : d), '')
